import React, { useCallback } from 'react';
import 'twin.macro';
import { EDTToolbarActions } from 'components/EDT/EDTToolbarActions';
import useUpdateEDT from 'queries/edt/useUpdateEDT';
import { EmploiDuTemps } from 'types/edt';
import { Classroom } from 'types/classroom';
import { useTranslation } from 'react-i18next';
import ShareEntity from 'components/ShareEntity/Loadable';
import { ClassroomConfiguration } from './ClassroomConfiguration';
import { DrawerView } from './types';
import { EDTEventsTagList } from 'components/TagList/EDTEventsTagList';
import { PencilIcon } from '@heroicons/react/24/outline';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { useNavigate } from 'react-router-dom';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';

interface Props {
  emploiDuTemps: EmploiDuTemps;
  onChangeDisplayedClassrooms: (v: string[]) => void;
  classrooms: Classroom[];
  displayedClassrooms: string[];
  drawerView?: DrawerView;
}

export function Body(props: Props) {
  const {
    emploiDuTemps,
    onChangeDisplayedClassrooms,
    classrooms,
    displayedClassrooms,
    drawerView,
  } = props;

  const { t } = useTranslation(['emploiDuTemps', 'common']);
  const updateEDT = useUpdateEDT();
  const navigate = useNavigate();
  const { schoolyear, school } = useSchoolSchoolyear();
  const onZoomChange = useCallback(
    step =>
      updateEDT({
        edt: {
          ...emploiDuTemps,
          step,
        },
      }),
    [JSON.stringify(emploiDuTemps)],
  );
  const onUpdateEndDate = useCallback(
    endDay =>
      updateEDT({
        edt: {
          ...emploiDuTemps,
          endDay,
        },
      }),
    [JSON.stringify(emploiDuTemps)],
  );

  const onUpdateStartDate = useCallback(
    startDay =>
      updateEDT({
        edt: {
          ...emploiDuTemps,
          startDay,
        },
      }),
    [JSON.stringify(emploiDuTemps)],
  );

  const onUpdateView = useCallback(
    view =>
      updateEDT({
        edt: {
          ...emploiDuTemps,
          view,
        },
      }),
    [JSON.stringify(emploiDuTemps)],
  );
  if (drawerView === 'volume') {
    return (
      <EDTEventsTagList
        classrooms={classrooms.filter(c => displayedClassrooms.includes(c.id))}
        events={emploiDuTemps.events}
        edtStartTime={emploiDuTemps.startDay}
        edtEndTime={emploiDuTemps.endDay}
        edtId={emploiDuTemps.id}
      />
    );
  }
  if (drawerView === 'share') {
    return (
      <div tw="-mx-5">
        <ShareEntity
          description={t('emploiDuTemps:sidebar.onlyAvailableWithLink')}
          entityId={emploiDuTemps.id}
          entityType="emploiDuTemps"
          entityUrl="emploi-du-temps"
        />
      </div>
    );
  }

  return (
    <div tw="flex flex-col gap-8">
      <div>
        <h4 tw="mb-4">{t('emploiDuTemps:sidebar.display')}</h4>
        <EDTToolbarActions
          onChangeDisplayedClassrooms={onChangeDisplayedClassrooms}
          onZoomChange={onZoomChange}
          onUpdateStartDate={onUpdateStartDate}
          onUpdateEndDate={onUpdateEndDate}
          onUpdateView={onUpdateView}
          currentView={emploiDuTemps.view}
          startTime={emploiDuTemps.startDay}
          endTime={emploiDuTemps.endDay}
          step={emploiDuTemps.step}
          classrooms={classrooms}
          displayedClassrooms={displayedClassrooms}
        />
      </div>

      <div>
        <div tw="flex items-center gap-x-2 mb-4">
          <h4>{t('emploiDuTemps:sidebar.classroomsConfiguration')}</h4>
          <Button
            onClick={() =>
              navigate(
                `/mes-annees-scolaires/${schoolyear.id}/ecoles/${school.id}/general`,
              )
            }
            small
            minimal
            intent="none"
            icon={PencilIcon}
            aria-label={t('common:buttons.edit', 'Éditer')}
          />
        </div>
        <div tw="space-y-6">
          {classrooms.map(classroom => (
            <ClassroomConfiguration
              key={classroom.id}
              classrooms={classrooms}
              classroom={classroom}
              index={classrooms.findIndex(c => c.id === classroom.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
